import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useSearchParams } from "react-router-dom";
import supabase from "../../../config/supabaseConfig";
import { useAuth } from "../../../Hooks/useAuth";
import { Ad } from "../../../types/adType";
import interleaveAds from "../components/balancedShuffleAds";

export type HomeTab = 'all' | 'premium' | 'verified';
export type HomeSort = '' | 'high' | 'low';

// "Vérifiés" tab switched off for now: it is shown greyed out and never loads.
// Its loading code below is kept (and only runs when the tab is opened), so
// turning it back on is just this line.
export const VERIFIED_TAB_ENABLED = false;

// 12 fills the grid evenly at 2, 3 or 4 columns
export const PAGE_SIZE = 12;
// Premium ads mixed into "Toutes les annonces": up to 6 per page (the 4 regular +
// 2 premium pattern on a 12-ad page), taken in turns from at most 30 premium ads.
// Same numbers as the search page.
const MAX_PREMIUM_FETCH = 30;
const PREMIUM_PER_PAGE = 6;

const AD_SELECT = `
    *,
    categories:category_id(name),
    subcategories:subcategory_id(name),
    states:state_id(name),
    cities:city_id(name),
    profiles:user_id(id, name, is_verified, photo_url),
    product_images(url)
`;
// verified sellers only: !inner drops ads whose seller doesn't match the filter
const VERIFIED_SELECT = AD_SELECT.replace("profiles:user_id(", "profiles:user_id!inner(");

type AdsKind = 'regular' | 'premium' | 'verified';

// One page of ads, plus the total for the page count. Sorted on the server, with
// the id as a tie-breaker so ads with the same date/price never swap between pages.
const fetchAdsPage = async (kind: AdsKind, sort: HomeSort, from: number, to: number) => {
    let query = supabase
        .from("ads")
        .select(kind === 'verified' ? VERIFIED_SELECT : AD_SELECT, { count: "exact" })
        .eq("is_deleted", false)
        .eq("is_sold", false);

    if (kind === 'regular') query = query.eq("is_premium", false);
    if (kind === 'premium') query = query.eq("is_premium", true);
    if (kind === 'verified') query = query.eq("profiles.is_verified", true);

    const orderColumn = sort === "high" || sort === "low" ? "price" : "published_at";
    const { data, error, count } = await query
        .order(orderColumn, { ascending: sort === "low", nullsFirst: false })
        .order("id")
        .range(from, to);

    if (error) throw error;
    return { ads: (data as unknown as Ad[]) || [], total: count ?? 0 };
};

// A random starting point, kept for the browser session: each visit puts different
// premium ads first, while going back to a page shows the same layout as before
// (so the scroll position still lands on the same ad).
const getRotationSeed = () => {
    try {
        const saved = Number(sessionStorage.getItem("homePremiumSeed"));
        if (Number.isInteger(saved) && saved > 0) return saved;
        const seed = 1 + Math.floor(Math.random() * 1_000_000);
        sessionStorage.setItem("homePremiumSeed", String(seed));
        return seed;
    } catch {
        return 1 + Math.floor(Math.random() * 1_000_000);
    }
};

// The premium ads for one page: each page continues where the previous one stopped,
// wrapping around, so every premium ad gets its turn (not only the newest ones).
const premiumForPage = (pool: Ad[], page: number, seed: number): Ad[] => {
    if (pool.length === 0) return [];
    const start = (seed + (page - 1) * PREMIUM_PER_PAGE) % pool.length;
    return [...pool.slice(start), ...pool.slice(0, start)].slice(0, PREMIUM_PER_PAGE);
};

export const useHomeAds = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // Page, tab and sort live in the address (/?page=2&tab=premium&sort=low), so the
    // back button and shared links come back to the same place.
    const pageParam = Number(searchParams.get("page"));
    const currentPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
    const tabParam = searchParams.get("tab");
    const activeTab: HomeTab =
        tabParam === "premium" || (tabParam === "verified" && VERIFIED_TAB_ENABLED) ? tabParam : "all";
    const sortParam = searchParams.get("sort");
    const sort: HomeSort = sortParam === "high" || sortParam === "low" ? sortParam : "";

    const [ads, setAds] = useState<Ad[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [fetchFailed, setFetchFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [displayAds, setDisplayAds] = useState<"grid" | "list">("grid");
    const [favorites, setFavorites] = useState<Set<string>>(new Set());
    const [isTogglingFavorite, setIsTogglingFavorite] = useState<Record<string, boolean>>({});
    const [rotationSeed] = useState(getRotationSeed);

    // The premium ads are loaded once per sort order, not again on every page
    const premiumPoolRef = useRef(new Map<HomeSort, Promise<Ad[]>>());
    const getPremiumPool = useCallback((sortOrder: HomeSort) => {
        const cached = premiumPoolRef.current.get(sortOrder);
        if (cached) return cached;
        const pool = fetchAdsPage('premium', sortOrder, 0, MAX_PREMIUM_FETCH - 1)
            .then(({ ads: premium }) => premium)
            .catch((err) => {
                // the regular ads still show; the premium ones are retried next time
                console.error("[useHomeAds] premium ads failed:", err);
                premiumPoolRef.current.delete(sortOrder);
                return [] as Ad[];
            });
        premiumPoolRef.current.set(sortOrder, pool);
        return pool;
    }, []);

    const updateParams = useCallback((changes: Record<string, string | null>, replace: boolean) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(changes).forEach(([key, value]) => {
                if (value) next.set(key, value);
                else next.delete(key);
            });
            return next;
        }, { replace });
    }, [setSearchParams]);

    // Load only the page being viewed (and only for the open tab)
    useEffect(() => {
        let cancelled = false;
        const from = (currentPage - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        const load = async () => {
            setIsLoading(true);
            setFetchFailed(false);
            try {
                let pageAds: Ad[];
                let total: number;
                if (activeTab === 'all') {
                    const [regular, premiumPool] = await Promise.all([
                        fetchAdsPage('regular', sort, from, to),
                        getPremiumPool(sort),
                    ]);
                    pageAds = interleaveAds(premiumForPage(premiumPool, currentPage, rotationSeed), regular.ads);
                    total = regular.total;
                } else {
                    ({ ads: pageAds, total } = await fetchAdsPage(activeTab === 'premium' ? 'premium' : 'verified', sort, from, to));
                }
                if (cancelled) return;

                const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
                // e.g. an old link to page 9 after ads were removed: go to the last page
                if (currentPage > pages) {
                    updateParams({ page: pages > 1 ? String(pages) : null }, true);
                    return;
                }
                setAds(pageAds);
                setTotalPages(pages);
                setTotalItems(total);
            } catch (err) {
                if (cancelled) return;
                console.error("[useHomeAds] loading ads failed:", err);
                setFetchFailed(true);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [activeTab, currentPage, sort, reloadKey, rotationSeed, getPremiumPool, updateParams]);

    const retryFetch = useCallback(() => {
        premiumPoolRef.current.clear();
        setReloadKey((key) => key + 1);
    }, []);

    // Changing page adds a history entry (back returns to the previous page) and
    // brings the top of the ads list into view
    const handlePageChange = useCallback((page: number) => {
        updateParams({ page: page > 1 ? String(page) : null }, false);
        const list = document.getElementById("home-ads");
        const top = list ? list.getBoundingClientRect().top + window.scrollY - 90 : 0;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, [updateParams]);

    // A new tab or sort starts again at page 1
    const setActiveTab = useCallback((tab: HomeTab) => {
        updateParams({ tab: tab === 'all' ? null : tab, page: null }, true);
    }, [updateParams]);

    const setSort = useCallback((value: string) => {
        updateParams({ sort: value === "high" || value === "low" ? value : null, page: null }, true);
    }, [updateParams]);

    const toggleFavorite = async (ad: Ad) => {
        if (!user?.id) {
            message.info("Veuillez vous connecter pour ajouter aux favoris");
            return;
        }

        const adId = ad.id;
        setIsTogglingFavorite(prev => ({ ...prev, [adId]: true }));

        try {
            const isFavorite = favorites.has(adId);

            if (isFavorite) {
                await supabase
                    .from("ad_favorites")
                    .delete()
                    .eq("user_id", user.id)
                    .eq("ad_id", adId);

                setFavorites(prev => {
                    const updated = new Set(prev);
                    updated.delete(adId);
                    return updated;
                });

                message.success("Retiré des favoris");
            } else {
                await supabase
                    .from("ad_favorites")
                    .insert({
                        user_id: user.id,
                        ad_id: adId,
                        created_at: new Date().toISOString()
                    });

                setFavorites(prev => {
                    const updated = new Set(prev);
                    updated.add(adId);
                    return updated;
                });

                message.success("Ajouté aux favoris");
            }
        } catch {
            message.error("Erreur lors de la mise à jour des favoris");
        } finally {
            setIsTogglingFavorite(prev => ({ ...prev, [adId]: false }));
        }
    };

    useEffect(() => {
        const fetchUserFavorites = async () => {
            if (user?.id) {
                try {
                    const { data, error } = await supabase
                        .from("ad_favorites")
                        .select("ad_id")
                        .eq("user_id", user.id);

                    if (error) throw error;

                    const favSet = new Set(data?.map(fav => fav.ad_id) || []);
                    setFavorites(favSet);
                } catch {
                    // Silently handle error - no need to show message for favorites fetch failure
                }
            }
        };

        fetchUserFavorites();
    }, [user]);

    return {
        // State
        ads,
        isLoading,
        fetchFailed,
        sort,
        displayAds,
        favorites,
        isTogglingFavorite,
        activeTab,
        currentPage,
        totalPages,
        totalItems,

        // Actions
        setSort,
        setDisplayAds,
        setActiveTab,
        toggleFavorite,
        handlePageChange,
        retryFetch,
    };
};
